import * as z from 'zod'

import { CreateArticleVars } from '@/common/api/params/articles'
import { StringLength } from '@/common/form/schema'
import {
  ARTICLE_PERSONA_MAX_LENGTH,
  ArticlePartRecord,
  ArticleProposalDetail,
  ArticleTone
} from '@/common/model/media/article'
import { GeneratedArticle, GeneratedOutline } from '@/common/model/media/articleCreator'

const internalLinkCandidateSchema = z.object({
  article_proposal_id: z.number(),
  title: z.string(),
  slug: z.string(),
  status: z.string(),
  selected: z.boolean()
})

export type InternalLinkCandidateSelection = z.infer<typeof internalLinkCandidateSchema>

export const articleCreateSchema = z.object({
  keyword: z.string().min(1, 'キーワードは必須です').max(StringLength.TEXT_MAX),
  title: z.string().max(StringLength.NORMAL_MAX),
  headingOutline: z.string().max(StringLength.TEXT_MAX),
  tone: z.custom<ArticleTone>(),
  persona: z.string().max(ARTICLE_PERSONA_MAX_LENGTH),
  targetCharCount: z.number().min(1000).max(5000),
  slug: z.string().max(StringLength.TEXT_MAX),
  metaDescription: z.string().max(StringLength.TEXT_MAX),
  bodyHtml: z.string().max(StringLength.HTML_MAX),
  bodyHtmlBottom: z.string().max(StringLength.HTML_MAX),
  pageTitle: z.string().max(StringLength.NORMAL_MAX),
  titleCandidate: z.string().max(StringLength.NORMAL_MAX),
  metaDescriptionCandidate: z.string().max(StringLength.TEXT_MAX),
  internalLinkCandidates: z.array(internalLinkCandidateSchema),
  // AI（自動記事作成）で本文を生成したか。保存時に articles.ai_used へ記録する
  aiUsed: z.boolean()
})

export type ArticleCreateSchemaType = z.infer<typeof articleCreateSchema>

export const DEFAULT_ARTICLE_CREATE_SCHEMA: ArticleCreateSchemaType = {
  keyword: '',
  title: '',
  headingOutline: '',
  tone: 'professional',
  persona: '',
  targetCharCount: 3000,
  slug: '',
  metaDescription: '',
  bodyHtml: '',
  bodyHtmlBottom: '',
  pageTitle: '',
  titleCandidate: '',
  metaDescriptionCandidate: '',
  internalLinkCandidates: [],
  aiUsed: false
}

// AI構成案生成結果 → フォーム値への反映（STEP02の入力項目のみ上書きする）
// ペルソナは構成案の応答に含まれないため上書きしない（先に入力した値を残す）
export const applyGeneratedOutlineToSchema = (
  current: ArticleCreateSchemaType,
  outline: GeneratedOutline
): ArticleCreateSchemaType => ({
  ...current,
  headingOutline: outline.heading_outline.length
    ? outline.heading_outline.join('\n')
    : current.headingOutline,
  tone: (outline.tone as ArticleTone) || current.tone,
  targetCharCount: outline.target_length || current.targetCharCount,
  titleCandidate: outline.title_candidates.length
    ? outline.title_candidates.join('\n')
    : current.titleCandidate,
  metaDescriptionCandidate: outline.meta_description || current.metaDescriptionCandidate,
  internalLinkCandidates: outline.internal_link_candidates.map((c) => ({ ...c, selected: true }))
})

// AI生成結果 → フォーム値への反映（本文内容ブロックの反映先のみ上書きする）
// 本文はAIがコンテンツエリア上部・下部それぞれのHTMLとして生成するため、そのまま反映する
// （登録された記事HTMLの型が反映されたHTMLなので、フロントで組み立て直さない）
export const applyGeneratedArticleToSchema = (
  current: ArticleCreateSchemaType,
  article: GeneratedArticle
): ArticleCreateSchemaType => ({
  ...current,
  title: article.title || current.title,
  // STEP02の「ページタイトル案」はあくまで生成プロンプトの参考情報であり、
  // STEP03の「ページタイトル」（正しいタイトル）は生成結果の title を初期値として独立に持つ
  pageTitle: article.title || current.pageTitle,
  metaDescription: article.meta_description || current.metaDescription,
  bodyHtml: article.content_area_top_html || current.bodyHtml,
  bodyHtmlBottom: article.content_area_bottom_html || current.bodyHtmlBottom,
  // AIで本文を自動生成したことを記録
  aiUsed: true
})

// フォーム値 → 記事新規作成パラメータ
export const convertSchemaToCreateVars = (
  siteId: number,
  data: ArticleCreateSchemaType,
  sourceActionId?: number | null
): CreateArticleVars => ({
  site_id: siteId,
  title: data.title.trim() || null,
  slug: data.slug.trim() || null,
  keyword: data.keyword.trim(),
  meta_description: data.metaDescription.trim() || null,
  heading_outline: data.headingOutline.trim() || null,
  tone: data.tone,
  persona: data.persona.trim() || null,
  target_char_count: data.targetCharCount,
  ai_used: data.aiUsed,
  source_media_action_id: sourceActionId ?? null,
  parts: [
    { part_type: 'content_area_top', content_html: data.bodyHtml },
    { part_type: 'content_area_bottom', content_html: data.bodyHtmlBottom },
    { part_type: 'page_title', page_title: data.pageTitle.trim() || null }
  ]
})

const findPart = (parts: ArticlePartRecord[], partType: string): ArticlePartRecord | undefined =>
  parts.find((p) => p.part_type === partType)

// コピー元記事 → 記事新規作成フォームの初期値（IDやステータス・承認情報等は引き継がない）
export const convertProposalDetailToCreateSchema = (
  detail: ArticleProposalDetail
): Partial<ArticleCreateSchemaType> => ({
  keyword: detail.latest_article.keyword ?? '',
  title: detail.latest_article.title ?? '',
  headingOutline: detail.latest_article.heading_outline ?? '',
  tone: detail.latest_article.tone ?? 'professional',
  persona: detail.latest_article.persona ?? '',
  targetCharCount: detail.latest_article.target_char_count ?? 3000,
  slug: detail.latest_article.slug ?? '',
  metaDescription: detail.latest_article.meta_description ?? '',
  bodyHtml: findPart(detail.parts, 'content_area_top')?.content_html ?? '',
  bodyHtmlBottom: findPart(detail.parts, 'content_area_bottom')?.content_html ?? '',
  pageTitle: findPart(detail.parts, 'page_title')?.page_title ?? ''
})
